import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "../../../shared/Badge";
import Button from "../../../shared/Button";
import Spinner from "../../../shared/Spinner";
import { useEvaluateManifestV2GateMutation } from "../../../redux/services/manifests/manifestsV2Api";
import { enrichGateIssue, READINESS_GATES } from "../utils";

/**
 * Compliance readiness checklist — previews gate results for a manifest.
 *
 * @param {object} props
 * @param {string} props.manifestId
 * @param {object[]} [props.legs]
 * @param {object} [props.storedEvaluations] - persisted gate results from manifest record
 */
const ReadinessChecklist = ({ manifestId, legs = [], storedEvaluations = {} }) => {
  const [evaluateGate, { isLoading }] = useEvaluateManifestV2GateMutation();
  const [results, setResults] = useState({});

  async function runChecks() {
    const next = {};
    for (const gate of READINESS_GATES) {
      try {
        const res = await evaluateGate({
          id: manifestId,
          body: { gate: gate.value },
        }).unwrap();
        next[gate.value] = res.evaluation || res.data?.evaluation;
      } catch (error) {
        next[gate.value] = {
          passed: false,
          blocks: [{ code: "EVAL_ERROR", message: error?.data?.message || "Check failed" }],
          warnings: [],
        };
      }
    }
    setResults(next);
  }

  useEffect(() => {
    if (manifestId) {
      runChecks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestId]);

  const displayResults = Object.keys(results).length ? results : storedEvaluations;

  function renderIssues(items = [], tone = "block") {
    if (!items.length) return null;

    const toneClass = tone === "warn" ? "text-amber-700" : "text-red-700";

    return (
      <ul className={`mt-2 space-y-2 text-sm ${toneClass}`}>
        {items.map((item, index) => {
          const enriched = enrichGateIssue(item, legs);
          return (
            <li key={`${item.code || "issue"}-${index}`}>
              <span>{enriched.message || enriched.code}</span>
              {enriched.jobPath ? (
                <>
                  {" "}
                  <Link className="font-medium underline" to={enriched.jobPath}>
                    Edit {enriched.legLabel}
                  </Link>
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Readiness checklist</h2>
          <p className="text-sm text-gray-500">
            Compliance gates for dispatch, depart, close, and payout.
          </p>
        </div>
        <Button type="alternative" size="sm" loading={isLoading} onClick={runChecks}>
          Refresh
        </Button>
      </div>

      {isLoading && !Object.keys(displayResults).length ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Spinner size="sm" />
          Running compliance checks...
        </div>
      ) : (
        <ul className="space-y-3">
          {READINESS_GATES.map((gate) => {
            const evaluation = displayResults[gate.value];
            const passed = evaluation?.passed;
            const blocks = evaluation?.blocks || [];
            const warnings = evaluation?.warnings || [];
            const hasBlocks = blocks.length > 0;
            const hasWarnings = warnings.length > 0;

            return (
              <li
                key={gate.value}
                className="rounded-md border border-gray-100 bg-gray-50 p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900">{gate.label}</span>
                  {evaluation ? (
                    <Badge
                      color={passed ? "green" : hasBlocks ? "red" : "amber"}
                      label={passed ? "Pass" : hasBlocks ? "Block" : "Warn"}
                    />
                  ) : (
                    <Badge color="gray" label="Not checked" />
                  )}
                </div>

                {renderIssues(blocks, "block")}
                {renderIssues(warnings, "warn")}
                {!hasBlocks && !hasWarnings && evaluation && !passed ? (
                  <p className="mt-2 text-sm text-amber-700">
                    Gate has open warnings — review before continuing.
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ReadinessChecklist;
